import type { ComponentPropsWithRef } from 'react';
import type { CSSInterpolation } from '@emotion/css';
import { css, cx } from '@emotion/css';
import { pxToRem } from '@mos-cat/ds-theme';
import { createComponent, createHook } from 'reakit-system';
import { variant } from 'styled-system';
import type { BoxHTMLProps, BoxProps, BoxOptions } from '../Box/Box';
import { useBox } from '../Box/Box';
import { ISLAND_SPACE, ISLAND_WRAPPER_PADDING } from '../Island/Island';
import { useStyles, useTheme } from '../hooks';

/**
 * @beta
 */

const paddings = [
  'sp-16',
  'contentPaddingX',
  pxToRem(24),
  'sp-32',
  pxToRem(33),
  pxToRem(36),
] as BoxProps['px'];

export const mode = variant({
  prop: 'mode',
  variants: {
    island: {
      bg: 'body',
      px: ISLAND_WRAPPER_PADDING,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      '& > * + *': {
        mt: ISLAND_SPACE,
      },
    },
    default: {
      bg: 'white',
      px: paddings,
    },
  },
});

export type Mode = 'island' | 'default';

export type ContentOptions = {
  mode?: Mode;
} & Omit<
  BoxOptions,
  'paddingX' | 'paddingLeft' | 'paddingRight' | 'px' | 'pl' | 'pr' | 'css'
>;

export const useContent = createHook<ContentOptions, BoxHTMLProps>({
  keys: ['mode'],
  name: 'Content',
  compose: [useTheme, useStyles, useBox],
  useOptions(options) {
    return {
      maxWidth: pxToRem(1600),
      mx: 'auto',
      ...options,
    };
  },
  useProps(options, props) {
    return {
      ...props,
      className: cx(
        props.className,
        css(
          {
            label: 'Content',
          },
          mode({
            ...options,
            mode: options.mode ?? 'default',
          }) as CSSInterpolation
        )
      ),
    };
  },
});

export const Content = createComponent({
  as: 'div',
  useHook: useContent,
});

export type ContentProps = ComponentPropsWithRef<typeof Content>;
